import React, { useState } from 'react';
import { QuizResponse, Question as QuestionType } from '../../types';
import { getNextQuestion } from './quizUtils';
import { Box, Button, Grid, Modal, Typography } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import { Question, QuizEnd } from '../Quiz';
import { useGlobalState } from '../../utils/GlobalStateProvider';
import { sendEvent } from '../../utils';
import { ANALYTICS_EVENTS } from '../../config';
import { useQuiz, useSubmitQuizResponse } from '../../config/api/queries';

const modalBox = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  borderRadius: 3,
  minWidth: { xs: '85%', md: '40%' },
  maxWidth: { xs: '85%', md: '50%' },
  minHeight: { xs: '40%', md: '40%' },
  maxHeight: { xs: '80%', md: '65%' },
  overflow: 'scroll',
  '&::-webkit-scrollbar': { width: 5 },
  '&::-webkit-scrollbar-track': { background: '#F8F8F8', borderRadius: 5 },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: 5 },
  '&::-webkit-scrollbar:horizontal': {
    height: 0,
    width: 0,
    display: 'none',
  },
};

export interface QuizProps {
  quizCode: string;
  bypassModal: boolean;
}

export const QuizWrapper = (props: QuizProps): JSX.Element => {
  const [modalOpen, setModalOpen] = useState<boolean>(true);
  const handleModalClose = () => setModalOpen(false);
  const [curIdx, setCurIdx] = useState<number>(0);
  const [fieldValue, setFieldValue] = useState<string>();
  const [formData, setFormData] = useState<Record<number, QuizResponse>>();
  const [error, setError] = useState<string>();
  const [completed, setCompleted] = useState<boolean>(false);
  const context = useGlobalState();
  const quizGet = useQuiz(props.quizCode);
  const quizResponse = useSubmitQuizResponse();

  const isValidResponse = (
    question: QuestionType,
    value: string | undefined
  ): boolean => {
    if (question.required && !value) {
      setError(question.error_message || 'This field is required');
      return false;
    } else if (
      question.min_amount &&
      value &&
      parseInt(value) < question.min_amount
    ) {
      setError(
        question.error_message ||
          `Please enter a value greater than or equal to ${question.min_amount}`
      );
      return false;
    } else if (
      question.max_amount &&
      value &&
      parseInt(value) > question.max_amount
    ) {
      setError(
        question.error_message ||
          `Please enter a value less than or equal to ${question.max_amount}`
      );
      return false;
    } else if (question.regex && value && !question.regex.test(value)) {
      setError(question.error_message || 'Input value is invalid');
      return false;
    }
    setError(undefined);
    return true;
  };

  const handleQuizComplete = () => {
    sendEvent(ANALYTICS_EVENTS.benefits_quiz_completed, quizGet.data);
    setCompleted(true);
    if (!props.bypassModal) {
      handleModalClose();
    }
  };

  const handleNext = (value?: string) => {
    if (quizGet.data?.questions) {
      let currentQuestion = quizGet.data.questions[curIdx];
      currentQuestion.render = true;
      quizGet.data.questions[curIdx] = currentQuestion;
      if (!isValidResponse(currentQuestion, value || fieldValue)) {
        return;
      }
      currentQuestion.value = value || fieldValue || '';
      const renderedQuestions =
        quizGet.data.questions.filter((q) => q.render === true) || [];
      const skippedQuestions =
        quizGet.data.questions.filter((q) => q.render === false) || [];
      const totalRenderedQuestions =
        quizGet.data.questions.length - skippedQuestions.length;
      const is_updating =
        formData && Object.keys(formData).includes(`${currentQuestion.id}`);
      const data: QuizResponse = {
        response: {
          question_id: currentQuestion.id,
          user_id: `${context.state.user?.id}`,
          answer: currentQuestion.value,
          quiz_id: quizGet.data?.id,
        },
        percent_completed: Math.round(
          (renderedQuestions.length / totalRenderedQuestions) * 100
        ),
        is_update: is_updating || false,
      };
      quizResponse.mutate(data);
      let formDataClone = formData || {};
      formDataClone[currentQuestion.id] = data;
      setFormData(formDataClone);

      setFieldValue(undefined);
      const nextQuestionIdx = getNextQuestion(
        curIdx + 1,
        quizGet.data?.questions,
        false
      );
      if (nextQuestionIdx !== curIdx + 1) {
        for (let i = curIdx + 1; i < nextQuestionIdx; i++) {
          quizGet.data.questions[i].render = false;
        }
      }
      if (quizGet.data?.questions?.length === nextQuestionIdx) {
        handleQuizComplete();
      } else {
        setCurIdx(nextQuestionIdx);
      }
    }
  };

  const handleBack = () => {
    if (quizGet.data?.questions) {
      const nextQuestionIdx = getNextQuestion(
        curIdx - 1,
        quizGet.data?.questions,
        true
      );
      if (formData) {
        setFieldValue(
          formData[quizGet.data.questions[nextQuestionIdx].id]?.response.answer
        );
      }
      setCurIdx(nextQuestionIdx);
    }
  };

  const handleResponse = (value: string, question: QuestionType) => {
    if (question.kind === 'single-select' && !value.startsWith('other: ')) {
      handleNext(value);
    } else {
      setFieldValue(value);
    }
  };

  const surveyContent = (): JSX.Element => {
    return (
      <Box sx={props.bypassModal ? {} : modalBox}>
        <Typography variant="light" sx={{ pl: 2 }}>
          {quizGet.data?.title}
        </Typography>
        {quizGet.data?.questions && !completed && (
          <>
            {curIdx - 1 !== quizGet.data.questions.length && (
              <Grid container justifyContent="center" sx={{ mt: 3 }}>
                <Question
                  key={curIdx}
                  question={quizGet.data.questions[curIdx]}
                  callback={handleResponse}
                  currentValue={
                    formData &&
                    formData[quizGet.data.questions[curIdx].id]?.response.answer
                  }
                />
              </Grid>
            )}
            <Grid container justifyContent="center" sx={{ my: 2 }}>
              {error && <Typography sx={{ color: 'red' }}>{error}</Typography>}
            </Grid>
            <Grid container justifyContent="center" sx={{ my: 2 }}>
              {curIdx > 0 && (
                <Button variant="outlined" onClick={() => handleBack()}>
                  Back
                </Button>
              )}
              {quizGet.data.questions.length !== curIdx - 1 && (
                <Button
                  variant="contained"
                  sx={{
                    ml: 1,
                    borderColor: 'info.main',
                    backgroundColor: 'info.main',
                  }}
                  onClick={() => handleNext()}
                  disabled={
                    quizGet.data.questions[curIdx].required && !fieldValue
                  }
                >
                  Next
                  <ArrowRightAltIcon fontSize="small" sx={{ pl: 1 }} />
                </Button>
              )}
            </Grid>
          </>
        )}
        {completed && quizGet.data && <QuizEnd quiz={quizGet.data} />}
      </Box>
    );
  };

  return (
    <>
      {!props.bypassModal && (
        <Modal open={modalOpen} onClose={handleModalClose}>
          {surveyContent()}
        </Modal>
      )}
      {props.bypassModal && surveyContent()}
    </>
  );
};
